import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { FeedPost } from "@gooner/shared";
import { track } from "../../../analytics";
import { userApi } from "../../../lib/api";
import { useFeedSound } from "../../../lib/feedSound";
import { formatWhen } from "../../lib/format";
import { Avatar } from "../../ui/Avatar";
import { GoldName } from "../../ui/Gold";
import { MediaSurface } from "../../ui/MediaSurface";
import { IconMute, IconStack, IconTrash, IconVolume } from "../../ui/icons";
import { cx } from "../../ui/primitives";
import { ClipEditModal } from "../ClipEditModal";
import { PosterPlaceholder } from "../ClipTile";
import { ConfirmDialog } from "../ConfirmDialog";
import { CommentThread } from "./CommentThread";
import { CrownPill } from "./CrownPill";
import { MASK } from "../../../lib/replayMask";

/**
 * One post: who, the thing to watch, the words, the crown, the thread. A clip renders
 * through MediaSurface (the sanctioned media element — safe mode and the unmount
 * teardown reach it): its poster off screen, playing looped while it is on screen
 * (owner, 2026-09-13: "videos should auto play when scrolled to them"), paused again as
 * it leaves — so only what is in view decodes. Sound is the PAGE's switch
 * (lib/feedSound): unmute once, every post after plays audible. A collection renders
 * as a card of its covers, linking to /browse.
 */
export function PostCard({
  post,
  onChange,
  onDeleted,
  openThread = false,
  eager = false,
}: {
  post: FeedPost;
  onChange: (next: FeedPost) => void;
  onDeleted: (id: string) => void;
  /** Start with the thread open — the single-post page. */
  openThread?: boolean;
  /** The first post, above the fold: its picture loads now. The rest load lazily. */
  eager?: boolean;
}) {
  const [thread, setThread] = useState(openThread);
  const [confirm, setConfirm] = useState(false);
  const [preview, setPreview] = useState(false);
  const goldClass = post.author.gold ? "text-[var(--color-accent-soft)]" : "text-zinc-100";

  const remove = async () => {
    setConfirm(false);
    try {
      await userApi.feedDelete(post.id);
      track("feed_post_deleted", { by: "author" });
      onDeleted(post.id);
    } catch {
      /* the next load tells the truth */
    }
  };

  return (
    <article className="overflow-hidden rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)]">
      <header className="flex items-center gap-2.5 px-3 py-2.5">
        <Avatar name={post.author.username} src={post.author.avatarUrl} size="sm" />
        <div className="min-w-0 flex-1">
          <Link to={`/u/${encodeURIComponent(post.author.username)}`} className={cx("text-sm font-medium hover:underline", goldClass)} {...MASK}>
            <GoldName name={post.author.username} gold={post.author.gold} />
          </Link>
          <div className="text-[11px] text-zinc-500">
            <Link to={`/feed/${post.id}`} className="hover:text-zinc-300" title={new Date(post.createdAt).toLocaleString()}>
              {formatWhen(post.createdAt)}
            </Link>
          </div>
        </div>
        {post.mine ? (
          <button
            type="button"
            onClick={() => setConfirm(true)}
            title="Delete this post"
            aria-label="Delete this post"
            className="rounded p-1 text-zinc-500 hover:bg-white/10 hover:text-zinc-200"
          >
            <IconTrash width={14} height={14} />
          </button>
        ) : null}
      </header>
      {post.pending ? (
        <p className="border-y border-amber-400/20 bg-amber-400/10 px-3 py-1.5 text-[11px] text-amber-300">
          In review — only you can see this until a moderator clears the upload.
        </p>
      ) : null}
      {post.collection ? <CollectionCard collection={post.collection} eager={eager} /> : post.item ? <PostMedia item={post.item} onOpen={() => setPreview(true)} eager={eager} /> : null}
      {/* Somebody else's words: masked in session replay (lib/replayMask). */}
      {post.caption ? <p className="whitespace-pre-wrap px-3 pt-2.5 text-sm text-zinc-200" {...MASK}>{post.caption}</p> : null}
      <footer className="flex items-center gap-2 px-3 py-2.5">
        {/* A collection has no clip to crown; its clips are crowned where they play. */}
        {post.item ? <CrownPill postId={post.id} crowns={post.crowns} onCrowns={(n) => onChange({ ...post, crowns: n })} /> : null}
        <button
          type="button"
          onClick={() => setThread((t) => !t)}
          aria-expanded={thread}
          className={cx(
            "rounded-full px-2.5 py-1 text-xs font-medium transition-colors",
            thread ? "bg-white/10 text-zinc-100" : "bg-[var(--color-panel-2)] text-zinc-300 hover:text-white",
          )}
        >
          {post.comments === 0 ? "Comment" : `${post.comments} comment${post.comments === 1 ? "" : "s"}`}
        </button>
      </footer>
      {thread ? (
        <CommentThread postId={post.id} onCount={(n) => onChange({ ...post, comments: n })} />
      ) : null}
      <ConfirmDialog
        open={confirm}
        title="Delete this post?"
        message="It comes off the Feed for everyone. The clip stays in your Faps or Uploads."
        onConfirm={() => void remove()}
        onCancel={() => setConfirm(false)}
      />
      {preview && post.item ? <ClipEditModal clip={post.item} onClose={() => setPreview(false)} /> : null}
    </article>
  );
}

/** How much of the box must be on screen before it plays. */
const PLAY_AT = 0.6;

/** The clip: its poster off screen; on screen it plays, looped and muted, with a sound
 *  toggle and a tap to pause. */
function PostMedia({ item, onOpen, eager }: { item: FeedPost["item"] & object; onOpen: () => void; eager: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [paused, setPaused] = useState(false);
  // Page-wide, not per post: unmute once and every post you scroll to plays with sound.
  const muted = useFeedSound((s) => s.muted);
  const setMuted = useFeedSound((s) => s.setMuted);
  useEffect(() => {
    const el = box.current;
    if (!el || item.kind !== "video" || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      ([e]) => {
        const on = !!e && e.intersectionRatio >= PLAY_AT;
        setInView(on);
        // Scrolling away and back resumes: a pause is for this visit to the post.
        if (!on) setPaused(false);
      },
      { threshold: [0, PLAY_AT, 1] },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [item.kind]);
  const playing = inView && !paused;
  // Portrait clips are the norm; a landscape one gets its own shape, bounded so a single
  // post never fills more than the viewport.
  const ratio = item.width && item.height ? Math.min(1.78, Math.max(0.8, item.width / item.height)) : 0.8;
  return (
    <div ref={box} className="relative bg-black" style={{ aspectRatio: String(ratio), maxHeight: "75vh" }}>
      {item.kind === "image" ? (
        // The post's own picture, so the master stays the fallback here (it is what was
        // posted) — but only fetched as it nears the screen.
        <MediaSurface kind="image" src={item.posterUrl ?? item.url} fit="contain" className="h-full w-full" lazy={!eager} />
      ) : playing ? (
        <MediaSurface kind="video" src={item.url} poster={item.posterUrl} fit="contain" loop muted={muted} className="h-full w-full" />
      ) : item.posterUrl ? (
        <MediaSurface kind="image" src={item.posterUrl} fit="contain" className="h-full w-full" lazy={!eager} />
      ) : (
        <MediaSurface kind="video" src={item.url} fit="contain" autoPlay={false} className="h-full w-full" />
      )}
      {item.kind === "video" ? (
        <button
          type="button"
          onClick={() => setPaused((p) => !p)}
          aria-label={playing ? "Pause" : "Play"}
          className="absolute inset-0"
        >
          {paused && inView ? (
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="rounded-full bg-black/55 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-sm">Paused</span>
            </span>
          ) : null}
        </button>
      ) : null}
      <div className="absolute bottom-2 right-2 flex items-center gap-1.5">
        {item.kind === "video" ? (
          <button
            type="button"
            onClick={() => setMuted(!muted)}
            aria-label={muted ? "Sound on" : "Sound off"}
            className="rounded-full bg-black/55 p-1.5 text-white hover:bg-black/75"
          >
            {muted ? <IconMute width={16} height={16} /> : <IconVolume width={16} height={16} />}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onOpen}
          className="rounded-full bg-black/55 px-2 py-1 text-[11px] font-medium text-white hover:bg-black/75"
        >
          View ↗
        </button>
      </div>
    </div>
  );
}

/** A shared collection: up to four covers, the name, the count, and the way in. */
function CollectionCard({ collection, eager }: { collection: NonNullable<FeedPost["collection"]>; eager: boolean }) {
  const covers = collection.covers.slice(0, 4);
  return (
    <Link to={`/browse/${encodeURIComponent(collection.id)}`} className="block bg-black" title="Open this collection">
      <div className={cx("grid gap-px", covers.length >= 2 ? "grid-cols-2" : "grid-cols-1")} style={{ aspectRatio: covers.length >= 2 ? "1" : "0.8", maxHeight: "75vh" }}>
        {covers.length === 0 ? (
          <div className="flex items-center justify-center text-zinc-600">
            <IconStack width={40} height={40} />
          </div>
        ) : (
          covers.map((c) => (
            <div key={c.id} className="relative overflow-hidden bg-zinc-900">
              {/* A cover is a poster or the placeholder — never the clip's master. */}
              {c.posterUrl ? (
                <MediaSurface kind="image" src={c.posterUrl} fit="cover" className="h-full w-full" lazy={!eager} />
              ) : (
                <PosterPlaceholder className="bg-zinc-900" />
              )}
            </div>
          ))
        )}
      </div>
      <div className="flex items-center gap-2 border-t border-[var(--color-edge)] bg-[var(--color-panel-2)] px-3 py-2">
        <IconStack width={16} height={16} className="shrink-0 text-zinc-400" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium text-zinc-100" {...MASK}>{collection.name}</span>
        <span className="shrink-0 text-xs text-zinc-500">
          {collection.clipCount} clip{collection.clipCount === 1 ? "" : "s"} · open ↗
        </span>
      </div>
    </Link>
  );
}
